import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
    FaPlus, FaTrash, FaEdit, FaGlobe, FaMoneyBillWave,
    FaCheckCircle, FaTimesCircle, FaSave, FaTimes
} from 'react-icons/fa'
import { supabase } from '../../../lib/supabase'
import { usePriceGroups } from '../../../lib/priceGroups'

function ZipChip({ zip, onRemove }) {
    return (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-medium rounded-full">
            {zip}
            {onRemove && (
                <button onClick={onRemove} className="hover:text-red-500 transition-colors">
                    <FaTimes size={8} />
                </button>
            )}
        </span>
    )
}

function GroupRow({ group, canEdit, onSave, onDelete }) {
    const { t } = useTranslation()
    const [editing, setEditing]   = useState(false)
    const [name,    setName]      = useState(group.name)
    const [zips,    setZips]      = useState(group.zip_codes || [])
    const [zipInput, setZipInput] = useState('')
    const [saving,  setSaving]    = useState(false)

    const handleAddZip = () => {
        const z = zipInput.trim()
        if (!z || zips.includes(z)) { setZipInput(''); return }
        setZips(prev => [...prev, z])
        setZipInput('')
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); handleAddZip() }
    }

    const handleSave = async () => {
        if (!name.trim()) return
        setSaving(true)
        try { await onSave({ id: group.id, name, zip_codes: zips }); setEditing(false) }
        catch (err) { alert(err.message) }
        finally { setSaving(false) }
    }

    const handleCancel = () => {
        setName(group.name); setZips(group.zip_codes || []); setZipInput(''); setEditing(false)
    }

    if (!editing) {
        return (
            <div className="flex gap-3 items-start p-4 bg-white rounded-xl border border-gray-100 shadow-sm group">
                <FaMoneyBillWave className="text-primary mt-0.5 shrink-0" size={14} />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 mb-1">{group.name}</p>
                    <div className="flex flex-wrap gap-1">
                        {(group.zip_codes || []).length === 0
                            ? <span className="text-[11px] text-gray-400 italic">{t('admin.estimator.pg_no_zips')}</span>
                            : group.zip_codes.map(z => <ZipChip key={z} zip={z} />)
                        }
                    </div>
                </div>
                {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-all" title={t('admin.estimator.pg_edit_group')}>
                            <FaEdit size={12} />
                        </button>
                        <button onClick={() => onDelete(group.id)} className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all" title={t('admin.estimator.pg_delete_group')}>
                            <FaTrash size={12} />
                        </button>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="p-4 bg-white rounded-xl border-2 border-primary/30 shadow-sm space-y-3">
            <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{t('admin.estimator.pg_group_name')}</label>
                <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value.toUpperCase())}
                    onBlur={async () => {
                        if (!name.trim()) return
                        const { data } = await supabase.from('price_groups').select('id').eq('name', name.trim().toUpperCase()).maybeSingle()
                        if (data && data.id !== group.id) {
                            alert(t('admin.error.duplicateName') || 'Group name already exists')
                        }
                    }}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 uppercase"
                    placeholder={t('admin.estimator.pg_name_placeholder')}
                />
            </div>
            <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                    <FaGlobe className="inline mr-1" size={10} />{t('admin.estimator.pg_zip_codes')}
                </label>
                <div className="flex flex-wrap gap-1 mb-2">
                    {zips.map(z => <ZipChip key={z} zip={z} onRemove={() => setZips(prev => prev.filter(x => x !== z))} />)}
                </div>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={zipInput}
                        onChange={e => setZipInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('admin.estimator.pg_zip_placeholder')}
                        className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <button onClick={handleAddZip} className="px-3 py-1.5 text-xs font-bold bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-all">
                        <FaPlus size={10} />
                    </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{t('admin.estimator.pg_zip_hint')}</p>
            </div>
            <div className="flex gap-2 justify-end">
                <button onClick={handleCancel} className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all">
                    {t('admin.estimator.cancel')}
                </button>
                <button onClick={handleSave} disabled={saving || !name.trim()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50">
                    <FaSave size={10} /> {saving ? t('admin.estimator.saving') : t('admin.estimator.save')}
                </button>
            </div>
        </div>
    )
}

export default function PriceGroupsManager({ canEdit }) {
    const { t } = useTranslation()
    const { groups, loading, error, upsertGroup, deleteGroup, reload } = usePriceGroups()
    const [addingNew, setAddingNew] = useState(false)
    const [newName,   setNewName]   = useState('')
    const [newZips,   setNewZips]   = useState([])
    const [zipInput,  setZipInput]  = useState('')
    const [saving,    setSaving]    = useState(false)

    const handleAddZipToNew = () => {
        const z = zipInput.trim()
        if (!z || newZips.includes(z)) { setZipInput(''); return }
        setNewZips(prev => [...prev, z])
        setZipInput('')
    }

    const handleCreateGroup = async () => {
        if (!newName.trim()) return
        setSaving(true)
        try {
            await upsertGroup({ name: newName, zip_codes: newZips })
            setAddingNew(false); setNewName(''); setNewZips([]); setZipInput('')
        } catch (err) { alert(err.message) }
        finally { setSaving(false) }
    }

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm(t('admin.estimator.pg_delete_confirm'))) return
        try { await deleteGroup(id) } catch (err) { alert(err.message) }
    }, [deleteGroup, t])

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                        <FaMoneyBillWave className="text-primary" size={16} />
                        {t('admin.estimator.pg_title')}
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">{t('admin.estimator.pg_subtitle')}</p>
                </div>
                {canEdit && !addingNew && (
                    <button
                        onClick={() => setAddingNew(true)}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-primary text-white rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                    >
                        <FaPlus size={10} /> {t('admin.estimator.pg_add_group')}
                    </button>
                )}
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                <FaGlobe className="mt-0.5 shrink-0" size={12} />
                <span>{t('admin.estimator.pg_info')}</span>
            </div>

            {/* Add new group inline form */}
            <AnimatePresence>
                {addingNew && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-3"
                    >
                        <div className="p-4 bg-white rounded-xl border-2 border-primary/40 shadow-sm space-y-3">
                            <p className="text-xs font-bold text-primary uppercase tracking-wide">{t('admin.estimator.pg_new_group')}</p>
                            <input
                                type="text"
                                value={newName}
                                onChange={e => setNewName(e.target.value.toUpperCase())}
                                placeholder={t('admin.estimator.pg_name_placeholder')}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 uppercase"
                                autoFocus
                            />
                            <div>
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {newZips.map(z => <ZipChip key={z} zip={z} onRemove={() => setNewZips(p => p.filter(x => x !== z))} />)}
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={zipInput}
                                        onChange={e => setZipInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); handleAddZipToNew() } }}
                                        placeholder={t('admin.estimator.pg_zip_placeholder')}
                                        className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                    <button onClick={handleAddZipToNew} className="px-3 py-1.5 text-xs font-bold bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-all">
                                        <FaPlus size={10} />
                                    </button>
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1">{t('admin.estimator.pg_zip_hint')}</p>
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button onClick={() => { setAddingNew(false); setNewName(''); setNewZips([]); setZipInput('') }} className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all">
                                    {t('admin.estimator.cancel')}
                                </button>
                                <button onClick={handleCreateGroup} disabled={saving || !newName.trim()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50">
                                    <FaSave size={10} /> {saving ? t('admin.estimator.saving') : t('admin.estimator.pg_create')}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Groups list */}
            {loading && (
                <div className="space-y-2">
                    {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
                </div>
            )}
            {!loading && error && <p className="py-10 text-sm text-center text-red-500">{error}</p>}
            {!loading && !error && groups.length === 0 && !addingNew && (
                <div className="py-14 text-center">
                    <FaMoneyBillWave className="mx-auto text-gray-300 mb-3" size={32} />
                    <p className="text-sm font-semibold text-gray-400">{t('admin.estimator.pg_empty_title')}</p>
                    <p className="text-xs text-gray-400 mt-1">{t('admin.estimator.pg_empty_body')}</p>
                </div>
            )}
            {!loading && !error && groups.length > 0 && (
                <div className="space-y-2">
                    {groups.map(g => (
                        <GroupRow
                            key={g.id}
                            group={g}
                            canEdit={canEdit}
                            onSave={upsertGroup}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
